import React from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import CircularProgress from "@mui/material/CircularProgress";
import { motion, AnimatePresence } from "framer-motion";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { useShipping } from "@/hooks/useShipping";
import { showError } from "@/utils/toast";

const steps = [
  { label: "Order Placed", Icon: ShoppingCartCheckoutIcon },
  { label: "Accepted", Icon: CheckCircleOutlineIcon },
  { label: "In Progress", Icon: Inventory2OutlinedIcon },
  { label: "On the Way", Icon: LocalShippingOutlinedIcon },
  { label: "Delivered", Icon: TaskAltOutlinedIcon },
];

const StepIcon = (props) => {
  const { active, completed, className, icon } = props;
  const idx = Number(icon) - 1;
  const IconComp = steps[idx]?.Icon || TaskAltOutlinedIcon;
  const base = "w-9 h-9 rounded-full grid place-items-center border";
  const state =
    completed || active
      ? "bg-slate-900/5 text-slate-900 border-slate-300"
      : "bg-white text-slate-500 border-slate-200";
  return (
    <span className={`${base} ${state} ${className}`}>
      <IconComp fontSize="small" />
    </span>
  );
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
  },
};

const OrderTracking = () => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [trackingInput, setTrackingInput] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading | shown
  const [orderItems, setOrderItems] = React.useState([]);
  const [trackingData, setTrackingData] = React.useState(null);
  const [orderData, setOrderData] = React.useState(null);
  const isShown = status === "shown";

  const {
    loading: shippingLoading,
    error: shippingError,
    trackShipmentByAWB,
  } = useShipping();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trackingInput.trim()) return;
    setStatus("loading");

    try {
      // Our API returns { tracking, order }
      const { tracking, order } = await trackShipmentByAWB(trackingInput);
      const shipment = tracking?.raw?.ShipmentData?.[0]?.Shipment;

      if (!shipment && !order) {
        throw new Error("Invalid tracking response: No shipment or order data");
      }

      let normalized;
      let currentStep = 0;

      if (shipment) {
        // Build milestones from scans
        const milestones = (shipment.Scans || []).map((scan) => {
          const s = scan.ScanDetail;
          const dt = new Date(s.ScanDateTime);
          return {
            label: s.Scan || s.StatusCode,
            date: dt.toLocaleDateString(),
            time: dt.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            location: s.ScannedLocation,
            statusCode: s.StatusCode,
          };
        });

        // Match to our stepper
        const stepMap = {
          Manifested: 0,
          Accepted: 1,
          "In Transit": 2,
          Dispatched: 3,
          Delivered: 4,
        };
        currentStep = stepMap[shipment.Status?.Status] ?? 0;

        // Normalized trackingData
        normalized = {
          orderId: shipment.ReferenceNo,
          invoiceAmount: shipment.InvoiceAmount,
          sender: shipment.SenderName,
          consignee: shipment.Consignee,
          estimatedDelivery: shipment.ExpectedDeliveryDate
            ? new Date(shipment.ExpectedDeliveryDate).toLocaleDateString()
            : null,
          milestones,
          currentStep,
        };
      } else if (order) {
        // Handle case where there's an order but no tracking info yet
        const orderStatusMap = {
          pending_payment: 0,
          processing: 0,
          shipped: 3,
          delivered: 4,
          cancelled: 0, // Or handle cancelled state separately
        };
        currentStep = orderStatusMap[order.status] ?? 0;
        normalized = {
          orderId: order.orderNumber,
          milestones: [],
          currentStep,
          orderDate: order.orderDate
            ? new Date(order.orderDate).toLocaleDateString()
            : null,
        };
      }

      setTrackingData(normalized);
      setOrderItems(order?.items || []);
      setOrderData(order || null);
      setStatus("shown");
      setActiveStep(normalized.currentStep);
    } catch (error) {
      console.error("Failed to track shipment", error);
      setStatus("idle");
      showError(
        "Failed to track shipment. Please check your tracking number and try again."
      );
    }
  };

  return (
    <div className="bg-white text-slate-900 min-h-screen px-4 sm:px-6">
      {/* Page header */}
      <div className="border-b border-slate-100 bg-slate-50/60">
        <div className="container py-8 text-center max-w-7xl mx-auto">
          <h1 className="font-display text-3xl">Track Your Order</h1>
          <div className="text-sm text-slate-500 mt-2">
            <span className="text-slate-400">Home</span>
            <span className="mx-2">/</span>
            <span className="text-slate-700">Track Your Order</span>
          </div>
        </div>
      </div>

      <div className="container py-8 max-w-7xl mx-auto space-y-6">
        {/* Order status & search */}
        <motion.div layout className="bg-white rounded-2xl p-6 shadow w-full">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <div className="font-medium">Order Status</div>
              <div className="text-slate-500 text-sm mt-1">
                Order ID:{" "}
                <span className="text-slate-700 font-medium">
                  {isShown ? trackingInput : "—"}
                </span>
              </div>
            </div>
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 w-full sm:w-auto"
            >
              <TextField
                placeholder="Tracking ID or AWB"
                size="small"
                value={trackingInput}
                onChange={(e) => setTrackingInput(e.target.value)}
                className="w-full"
              />
              <Button
                variant="contained"
                size="medium"
                type="submit"
                disabled={status === "loading" || shippingLoading}
              >
                {status === "loading" || shippingLoading
                  ? "Checking…"
                  : "Track"}
              </Button>
            </form>
          </div>

          {/* Loading */}
          <AnimatePresence initial={false}>
            {(status === "loading" || shippingLoading) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center py-10"
              >
                <CircularProgress size={28} className="mr-3" />
                <span className="text-slate-600">
                  Fetching tracking details…
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stepper */}
          <AnimatePresence>
            {isShown && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="mt-6 overflow-x-auto"
              >
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  className="min-w-[500px] sm:min-w-full"
                >
                  {steps.map((s, i) => (
                    <Step key={s.label}>
                      <StepLabel StepIconComponent={StepIcon}>
                        <div className="text-slate-800 text-sm font-medium">
                          {s.label}
                        </div>
                        <div className="text-[12px] text-slate-500">
                          <span>
                            {trackingData?.milestones?.[i]?.date ? (
                              <>
                                {trackingData?.milestones?.[i]?.date}
                                {trackingData?.milestones?.[i]?.time
                                  ? ` • ${trackingData?.milestones?.[i]?.time}`
                                  : ""}
                              </>
                            ) : i === 0 && trackingData?.orderDate ? (
                              trackingData.orderDate
                            ) : trackingData?.estimatedDelivery ? (
                              `ETA ${trackingData?.estimatedDelivery}`
                            ) : (
                              "—"
                            )}
                          </span>
                        </div>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Order Summary */}
        <AnimatePresence>
          {isShown && orderData && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-2xl p-6 shadow w-full"
            >
              <div className="font-medium mb-4">Order Summary</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div>
                    <span className="font-medium">Order Number:</span>{" "}
                    {orderData.orderNumber}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>{" "}
                    {orderData.status}
                  </div>
                  <div>
                    <span className="font-medium">Payment:</span>{" "}
                    {orderData.paymentStatus}
                  </div>
                  <div>
                    <span className="font-medium">Total:</span> ₹
                    {orderData.totalAmount}
                  </div>
                </div>
                <div>
                  <div className="font-medium">Shipping Address</div>
                  <div>{orderData.shippingAddress?.street}</div>
                  <div>
                    {orderData.shippingAddress?.city},{" "}
                    {orderData.shippingAddress?.state}
                  </div>
                  <div>
                    {orderData.shippingAddress?.postalCode},{" "}
                    {orderData.shippingAddress?.country}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Products */}
        <AnimatePresence>
          {isShown && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0 }}
              className="bg-white rounded-2xl p-6 shadow w-full"
            >
              <motion.div variants={itemVariants} className="font-medium mb-4">
                Products
              </motion.div>
              <div className="divide-y divide-slate-100">
                {orderItems.map((item, idx) => (
                  <motion.div
                    variants={itemVariants}
                    key={idx}
                    className="py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">
                        {item.name}
                      </div>
                      <div className="text-slate-500 text-sm">
                        {item.quantity} Qty • ₹{item.price}
                      </div>
                      <div className="text-slate-700 text-sm font-medium">
                        Total: ₹{item.total}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Benefits */}
        <AnimatePresence>
          {isShown && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 text-center"
            >
              <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50">
                <CreditCardIcon className="text-slate-700 mx-auto" />
                <div className="mt-2 font-medium">Flexible Payment</div>
                <div className="text-slate-500 text-sm">
                  Multiple secure payment options
                </div>
              </div>
              <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50">
                <HeadsetMicIcon className="text-slate-700 mx-auto" />
                <div className="mt-2 font-medium">24x7 Support</div>
                <div className="text-slate-500 text-sm">
                  We support online all days
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OrderTracking;
